/**
 * AI-Guided Relationship Wizard
 * Step-by-step process where AI identifies key columns and guides user through validating relationships
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { 
  Wand2, Loader2, Check, X, ChevronRight, ChevronLeft, 
  Link2, Database, Server, SkipForward, CheckCircle2, 
  AlertCircle, Search, Edit2, RefreshCw, GitBranch, Ban
} from 'lucide-react';
import { 
  discoverKeyColumns, validateRelationship, saveTableRelationship,
  getTableRelationships, getSchemaStructure, getLearnedCorrections, saveLearnedCorrection,
  saveWrongRelation
} from '../../services/api';

const RelationshipWizard = ({ onComplete, isAdmin }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [state, setState] = useState({
    step: 'idle', // idle, discovering, select-key, validating, complete
    loading: false,
    
    // Key columns discovered by AI
    keyColumns: [], // [{column: 'user_id', sourceTable: 'users', sourceColumn: 'id', foundIn: ['orders', 'transactions', ...], count: 45}]
    currentKeyIndex: 0,
    
    // Current key being validated
    currentKey: null,
    tablesToValidate: [],
    currentTableIndex: 0,
    
    // Validation results
    confirmedRelationships: [],
    skippedTables: [],
    correctedRelationships: [],
    
    // LEARNED CORRECTIONS - When user corrects a key once, remember it for future relationships
    // Format: { 'user_id': { server: 'X', database: 'Y', table: 'accounts', column: 'id' } }
    learnedCorrections: {},
    
    // Schema for corrections
    schemaStructure: {},
    servers: [],
    
    // Stats
    totalProcessed: 0,
    totalConfirmed: 0,
    totalSkipped: 0,
    totalCorrected: 0,
    totalWrongRelations: 0, // Track wrong relations marked by user
    
    // Complex mapping modal
    showComplexMappingForm: false,
    
    // Existing relationships
    existingRelationships: []
  });

  const loadInitialData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const [rels, schema, corrections] = await Promise.all([
        getTableRelationships(),
        getSchemaStructure(),
        getLearnedCorrections()
      ]);
      
      const servers = Object.keys(schema.structure || {});
      
      setState(prev => ({
        ...prev,
        loading: false,
        existingRelationships: rels.relationships || [],
        schemaStructure: schema.structure || {},
        servers,
        // Load shared corrections from database
        learnedCorrections: corrections.corrections || {}
      }));
      
      // Calculate progress
      const relCount = rels.relationships?.length || 0;
      onCompleteRef.current(relCount > 0 ? Math.min(100, relCount * 5) : 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load schema data');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const startDiscovery = async () => {
    setState(prev => ({ ...prev, step: 'discovering', loading: true }));
    
    try {
      console.log('[AI Wizard] Starting key column discovery...');
      // Call AI to discover key columns
      const result = await discoverKeyColumns();
      console.log('[AI Wizard] Discovery result:', result);
      
      if (result.keyColumns && result.keyColumns.length > 0) {
        setState(prev => ({
          ...prev,
          loading: false,
          keyColumns: result.keyColumns,
          step: 'select-key'
        }));
        toast.success(`AI found ${result.keyColumns.length} key columns to validate`);
      } else {
        setState(prev => ({ ...prev, loading: false, step: 'idle' }));
        // More informative message based on result
        const totalTables = result.totalTables || 0;
        const existingRels = result.existingRelationships || 0;
        if (existingRels > 0 && totalTables > 0) {
          toast.info(`All ${existingRels} existing relationships cover all key columns. Schema is fully mapped!`, { duration: 5000 });
        } else if (totalTables === 0) {
          toast.warning('No schema found. Please discover your database schema first.', { duration: 5000 });
        } else {
          toast.info('No new key columns found to map. Your schema may already be fully configured.');
        }
      }
    } catch (error) {
      console.error('[AI Wizard] Discovery failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to discover key columns: ${errorMsg}`);
      setState(prev => ({ ...prev, loading: false, step: 'idle' }));
    }
  };

  const selectKeyColumn = (keyIndex) => {
    const key = state.keyColumns[keyIndex];
    setState(prev => ({
      ...prev,
      currentKeyIndex: keyIndex,
      currentKey: key,
      tablesToValidate: key.foundIn || [],
      currentTableIndex: 0,
      step: 'validating'
    }));
  };

  const handleConfirm = async () => {
    const key = state.currentKey;
    const table = state.tablesToValidate[state.currentTableIndex];
    
    // Check if we have a learned correction for this key column IN THE SAME DATABASE
    // Key format: "database|column" to scope corrections to same database
    const correctionKey = `${table.database}|${key.column}`;
    const learnedSource = state.learnedCorrections[correctionKey];
    
    // Use learned correction if available, otherwise use AI suggestion
    const sourceInfo = learnedSource || {
      server: key.sourceServer || state.servers[0],
      database: key.sourceDatabase,
      table: key.sourceTable,
      column: key.sourceColumn
    };
    
    const relationship = {
      source_server: sourceInfo.server,
      source_database: sourceInfo.database,
      source_table: sourceInfo.table,
      source_column: sourceInfo.column,
      target_server: table.server || state.servers[0],
      target_database: table.database,
      target_table: table.table,
      target_column: table.column || key.column,
      description: learnedSource ? `${key.column} relationship (using learned correction)` : `${key.column} relationship`
    };

    try {
      await saveTableRelationship(relationship);
      
      setState(prev => ({
        ...prev,
        confirmedRelationships: [...prev.confirmedRelationships, relationship],
        totalConfirmed: prev.totalConfirmed + 1,
        totalProcessed: prev.totalProcessed + 1
      }));
      
      toast.success(`✓ Saved: ${table.table}.${table.column || key.column} → ${sourceInfo.table}.${sourceInfo.column}`);
      moveToNextTable();
    } catch (error) {
      toast.error('Failed to save relationship');
    }
  };

  const handleSkip = () => {
    const table = state.tablesToValidate[state.currentTableIndex];
    
    setState(prev => ({
      ...prev,
      skippedTables: [...prev.skippedTables, { ...table, keyColumn: prev.currentKey?.column }],
      totalSkipped: prev.totalSkipped + 1,
      totalProcessed: prev.totalProcessed + 1
    }));
    
    moveToNextTable();
  };

  // Mark a relationship as WRONG - AI should never use or suggest this again
  const handleWrongRelation = async () => {
    const key = state.currentKey;
    const table = state.tablesToValidate[state.currentTableIndex];
    
    try {
      // Save as a "wrong_relation" in the database
      const wrongRelation = {
        source_column: key.column,
        source_table: key.tables?.[0]?.table || 'unknown',
        source_database: key.tables?.[0]?.database || 'unknown',
        target_server: table.server || state.servers[0],
        target_database: table.database,
        target_table: table.table,
        target_column: table.column || key.column,
        marked_wrong_at: new Date().toISOString(),
        reason: 'User marked as incorrect relationship'
      };
      
      // Call API to save wrong relation
      await saveWrongRelation(wrongRelation);
      
      toast.success('Marked as wrong relation - AI will not use this');
      
      setState(prev => ({
        ...prev,
        totalWrongRelations: prev.totalWrongRelations + 1,
        totalProcessed: prev.totalProcessed + 1
      }));
      
      moveToNextTable();
    } catch (error) {
      console.error('Failed to save wrong relation:', error);
      toast.error('Failed to mark as wrong relation');
    }
  };

  const handleCorrect = async (correctedSource) => {
    const key = state.currentKey;
    const table = state.tablesToValidate[state.currentTableIndex];
    
    const relationship = {
      source_server: correctedSource.server,
      source_database: correctedSource.database,
      source_table: correctedSource.table,
      source_column: correctedSource.column,
      target_server: table.server || state.servers[0],
      target_database: table.database,
      target_table: table.table,
      target_column: table.column || key.column,
      description: `Corrected: ${key.column} relationship`
    };

    try {
      // Save the relationship
      await saveTableRelationship(relationship);
      
      // SAVE learned correction to database (shared across all admins)
      const learnedCorrection = {
        server: correctedSource.server,
        database: correctedSource.database,
        table: correctedSource.table,
        column: correctedSource.column
      };
      
      // Key format: "database|column" to scope corrections to same database
      const correctionKey = `${table.database}|${key.column}`;
      
      // Save to backend so all admins see this correction
      await saveLearnedCorrection({
        database: table.database,
        column: key.column,
        source_server: correctedSource.server,
        source_database: correctedSource.database,
        source_table: correctedSource.table,
        source_column: correctedSource.column
      });
      
      setState(prev => ({
        ...prev,
        correctedRelationships: [...prev.correctedRelationships, relationship],
        totalCorrected: prev.totalCorrected + 1,
        totalProcessed: prev.totalProcessed + 1,
        showCorrectionForm: false,
        // Update local state with the correction
        learnedCorrections: {
          ...prev.learnedCorrections,
          [correctionKey]: learnedCorrection
        }
      }));
      
      toast.success(`✓ Saved & Shared: All admins will see ${key.column} in ${table.database} → ${correctedSource.table}.${correctedSource.column}`);
      moveToNextTable();
    } catch (error) {
      toast.error('Failed to save relationship');
    }
  };

  const moveToNextTable = () => {
    const nextIndex = state.currentTableIndex + 1;
    
    if (nextIndex >= state.tablesToValidate.length) {
      // Done with this key, move to next key or complete
      const nextKeyIndex = state.currentKeyIndex + 1;
      
      if (nextKeyIndex >= state.keyColumns.length) {
        // All done!
        setState(prev => ({ ...prev, step: 'complete' }));
        toast.success('🎉 Relationship wizard complete!');
      } else {
        // Move to next key selection
        setState(prev => ({
          ...prev,
          currentKeyIndex: nextKeyIndex,
          currentKey: null,
          tablesToValidate: [],
          currentTableIndex: 0,
          step: 'select-key'
        }));
      }
    } else {
      setState(prev => ({ ...prev, currentTableIndex: nextIndex }));
    }
  };

  const skipCurrentKey = () => {
    const nextKeyIndex = state.currentKeyIndex + 1;
    
    if (nextKeyIndex >= state.keyColumns.length) {
      setState(prev => ({ ...prev, step: 'complete' }));
    } else {
      setState(prev => ({
        ...prev,
        currentKeyIndex: nextKeyIndex,
        currentKey: null,
        step: 'select-key'
      }));
    }
  };

  // Render functions
  const renderIdleState = () => (
    <div className="text-center py-8">
      <Wand2 className="w-16 h-16 text-purple-400 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-800 mb-2">AI-Guided Relationship Discovery</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        The AI will identify key columns (like user_id, brand_id) and guide you through 
        validating relationships table by table.
      </p>
      <Button 
        onClick={startDiscovery} 
        className="bg-purple-500 hover:bg-purple-600"
        disabled={state.loading}
      >
        {state.loading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Wand2 className="w-4 h-4 mr-2" />
        )}
        Start AI Discovery
      </Button>
      
      {state.existingRelationships.length > 0 && (
        <p className="text-sm text-gray-400 mt-4">
          You have {state.existingRelationships.length} existing relationships
        </p>
      )}
    </div>
  );

  const renderDiscovering = () => (
    <div className="text-center py-12">
      <Loader2 className="w-12 h-12 text-purple-500 mx-auto mb-4 animate-spin" />
      <h3 className="text-lg font-medium text-gray-700">AI is analyzing your schema...</h3>
      <p className="text-gray-500 mt-2">Finding key columns and potential relationships</p>
    </div>
  );

  const renderKeySelection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">Select a Key Column to Validate</h3>
        <span className="text-sm text-gray-500">
          {state.currentKeyIndex + 1} of {state.keyColumns.length} keys
        </span>
      </div>
      
      <div className="grid gap-3">
        {state.keyColumns.map((key, idx) => (
          <div
            key={idx}
            onClick={() => selectKeyColumn(idx)}
            className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-purple-300 hover:bg-purple-50 ${
              idx < state.currentKeyIndex ? 'opacity-50 bg-gray-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-800">{key.column}</div>
                  <div className="text-sm text-gray-500">
                    Source: <span className="text-purple-600">{key.sourceTable}.{key.sourceColumn}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-800">{key.foundIn?.length || 0}</div>
                <div className="text-xs text-gray-500">tables to validate</div>
              </div>
            </div>
            {idx < state.currentKeyIndex && (
              <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Completed
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderValidation = () => {
    const key = state.currentKey;
    const table = state.tablesToValidate[state.currentTableIndex];
    const progress = ((state.currentTableIndex) / state.tablesToValidate.length) * 100;

    if (!key || !table) return null;

    // Check if we have a learned correction for this key IN THE SAME DATABASE
    const correctionKey = `${table.database}|${key.column}`;
    const learnedSource = state.learnedCorrections[correctionKey];
    const sourceInfo = learnedSource || {
      server: key.sourceServer || state.servers[0],
      database: key.sourceDatabase,
      table: key.sourceTable,
      column: key.sourceColumn
    };

    return (
      <div className="space-y-6">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Validating <span className="font-semibold text-purple-600">{key.column}</span> relationships
              <span className="text-gray-400 ml-1">in {table.database}</span>
            </span>
            <span className="text-gray-500">
              {state.currentTableIndex + 1} of {state.tablesToValidate.length}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Learned correction notice */}
        {learnedSource && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-amber-600" />
            <div className="text-sm">
              <span className="font-medium text-amber-800">Using team correction for {table.database}:</span>
              <span className="text-amber-600 ml-1">
                {key.column} → {learnedSource.table}.{learnedSource.column}
              </span>
              <span className="text-xs text-amber-500 ml-2">(AI suggested: {key.sourceTable})</span>
            </div>
          </div>
        )}

        {/* Current validation card */}
        <Card className="border-2 border-purple-200">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-2">Is this relationship correct?</p>
              <p className="text-xs text-gray-400 mb-4">
                The <span className="text-green-600 font-medium">green SOURCE</span> table contains the primary data. 
                The <span className="text-blue-600 font-medium">blue TARGET</span> table references it via foreign key.
              </p>
              
              {/* Visual representation */}
              <div className="flex items-center justify-center gap-4 py-4">
                <div className={`p-4 border-2 rounded-lg min-w-[200px] ${learnedSource ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-200'}`}>
                  <div className={`text-xs mb-2 font-semibold uppercase tracking-wide ${learnedSource ? 'text-amber-600' : 'text-green-600'}`}>
                    Source (Primary Key Table)
                    {learnedSource && <span className="ml-1 normal-case">(corrected)</span>}
                  </div>
                  <div className="space-y-1 text-left">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Server className="w-3 h-3" />
                      <span className="font-medium">{sourceInfo.server || 'Server'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Database className="w-3 h-3" />
                      <span className="font-medium">{sourceInfo.database || 'Database'}</span>
                    </div>
                  </div>
                  <div className={`font-bold mt-2 text-lg ${learnedSource ? 'text-amber-800' : 'text-green-800'}`}>{sourceInfo.table}</div>
                  <div className={`text-sm rounded px-2 py-0.5 inline-block ${learnedSource ? 'text-amber-600 bg-amber-100' : 'text-green-600 bg-green-100'}`}>.{sourceInfo.column}</div>
                  <div className={`text-xs mt-1 ${learnedSource ? 'text-amber-500' : 'text-green-500'}`}>
                    {learnedSource ? '(from your correction)' : '(click "Wrong Source" to change)'}
                  </div>
                </div>
                
                <div className="flex flex-col items-center">
                  <ChevronLeft className="w-8 h-8 text-purple-400" />
                  <div className="text-sm text-purple-600 font-semibold bg-purple-100 px-2 py-1 rounded">{key.column}</div>
                  <div className="text-xs text-gray-400 mt-1">references</div>
                </div>
                
                <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg min-w-[200px]">
                  <div className="text-xs text-blue-600 mb-2 font-semibold uppercase tracking-wide">
                    Target (Foreign Key Table)
                  </div>
                  <div className="space-y-1 text-left">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Server className="w-3 h-3" />
                      <span className="font-medium">{table.server || state.servers[0] || 'Server'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Database className="w-3 h-3" />
                      <span className="font-medium">{table.database || 'Database'}</span>
                    </div>
                  </div>
                  <div className="font-bold text-blue-800 mt-2 text-lg">{table.table}</div>
                  <div className="text-sm text-blue-600 bg-blue-100 rounded px-2 py-0.5 inline-block">.{table.column || key.column}</div>
                  <div className="text-xs text-blue-500 mt-1">(this stays fixed)</div>
                </div>
              </div>

              {/* AI explanation */}
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 mb-4">
                <strong>AI says:</strong> Each record in <code className="bg-blue-100 px-1 rounded">{table.table}</code> references 
                a record in <code className={`px-1 rounded ${learnedSource ? 'bg-amber-100' : 'bg-green-100'}`}>{sourceInfo.table}</code> via the <code className="bg-purple-100 px-1 rounded">{key.column}</code> column.
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={handleConfirm}
                className="bg-green-500 hover:bg-green-600 px-6"
              >
                <Check className="w-4 h-4 mr-2" />
                Correct
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setState(prev => ({ ...prev, showCorrectionForm: true }))}
                className="border-amber-300 text-amber-600 hover:bg-amber-50"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Wrong Source - Fix It
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setState(prev => ({ ...prev, showComplexMappingForm: true }))}
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                title="Use this when the target depends on another column's value"
              >
                <GitBranch className="w-4 h-4 mr-2" />
                Conditional Mapping
              </Button>
              
              <Button
                variant="outline"
                onClick={handleSkip}
                title="Skip for now - you can review skipped items at the end"
              >
                <SkipForward className="w-4 h-4 mr-2" />
                Skip (Review Later)
              </Button>
              
              <Button
                variant="outline"
                onClick={handleWrongRelation}
                className="border-red-300 text-red-600 hover:bg-red-50"
                title="Mark this as a WRONG relationship - AI will never use or suggest this again"
              >
                <Ban className="w-4 h-4 mr-2" />
                Wrong Relation
              </Button>
            </div>

            {/* Correction form */}
            {state.showCorrectionForm && (
              <CorrectionForm 
                schemaStructure={state.schemaStructure}
                servers={state.servers}
                currentKey={state.currentKey}
                currentTable={state.tablesToValidate[state.currentTableIndex]}
                learnedSource={learnedSource}
                onSave={handleCorrect}
                onCancel={() => setState(prev => ({ ...prev, showCorrectionForm: false }))}
              />
            )}
            
            {/* Complex Mapping form */}
            {state.showComplexMappingForm && (
              <ComplexMappingInlineForm
                currentKey={state.currentKey}
                currentTable={state.tablesToValidate[state.currentTableIndex]}
                onSave={() => {
                  setState(prev => ({ ...prev, showComplexMappingForm: false }));
                  toast.success('Complex mapping saved! This table will be skipped.');
                  handleSkip(); // Skip this table since it's a conditional mapping
                }}
                onCancel={() => setState(prev => ({ ...prev, showComplexMappingForm: false }))}
              />
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span>{state.totalConfirmed} confirmed</span>
          </div>
          <div className="flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-amber-500" />
            <span>{state.totalCorrected} corrected</span>
          </div>
          <div className="flex items-center gap-2">
            <SkipForward className="w-4 h-4 text-gray-400" />
            <span>{state.totalSkipped} skipped</span>
          </div>
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-500" />
            <span>{state.totalWrongRelations} wrong</span>
          </div>
        </div>

        {/* Skip this key */}
        <div className="text-center">
          <Button variant="ghost" size="sm" onClick={skipCurrentKey}>
            Skip all remaining for "{key.column}" →
          </Button>
        </div>
      </div>
    );
  };

  const renderComplete = () => (
    <div className="text-center py-8">
      <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-800 mb-2">Wizard Complete!</h3>
      <p className="text-gray-500 mb-6">
        Successfully processed {state.totalProcessed} tables
      </p>
      
      <div className="flex items-center justify-center gap-8 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{state.totalConfirmed}</div>
          <div className="text-sm text-gray-500">Confirmed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-600">{state.totalCorrected}</div>
          <div className="text-sm text-gray-500">Corrected</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-400">{state.totalSkipped}</div>
          <div className="text-sm text-gray-500">Skipped</div>
        </div>
      </div>

      {/* Review Skipped Tables */}
      {state.skippedTables.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-2xl mx-auto">
          <h4 className="font-medium text-yellow-800 mb-3 flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4" />
            You skipped {state.skippedTables.length} relationships
          </h4>
          <div className="max-h-40 overflow-y-auto mb-3">
            <div className="text-sm text-left space-y-1">
              {state.skippedTables.map((t, idx) => (
                <div key={idx} className="text-gray-600 px-2 py-1 bg-white rounded">
                  • {t.server}.{t.database}.<span className="font-medium">{t.table}</span>.{t.column}
                </div>
              ))}
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            className="border-yellow-400 text-yellow-700 hover:bg-yellow-100"
            onClick={() => {
              // Re-validate skipped tables
              setState(prev => ({
                ...prev,
                tablesToValidate: prev.skippedTables,
                currentTableIndex: 0,
                skippedTables: [],
                totalSkipped: 0,
                step: 'validating'
              }));
              toast.info('Reviewing skipped relationships...');
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Review Skipped Items
          </Button>
        </div>
      )}

      <div className="flex items-center justify-center gap-3">
        <Button onClick={() => {
          setState(prev => ({
            ...prev,
            step: 'idle',
            totalProcessed: 0,
            totalConfirmed: 0,
            totalSkipped: 0,
            totalCorrected: 0,
            keyColumns: [],
            currentKeyIndex: 0,
            skippedTables: []
          }));
          loadInitialData();
        }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Start Fresh Discovery
        </Button>
      </div>
    </div>
  );

  if (state.loading && state.step === 'idle') {
    return (
      <Card className="bg-white">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          <span className="ml-3 text-gray-600">Loading schema...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-purple-600" />
          AI-Guided Relationship Wizard
        </CardTitle>
      </CardHeader>
      <CardContent>
        {state.step === 'idle' && renderIdleState()}
        {state.step === 'discovering' && renderDiscovering()}
        {state.step === 'select-key' && renderKeySelection()}
        {state.step === 'validating' && renderValidation()}
        {state.step === 'complete' && renderComplete()}
      </CardContent>
    </Card>
  );
};

// Correction form component - User is correcting the SOURCE (left side / green box)
const CorrectionForm = ({ schemaStructure, servers, currentKey, currentTable, learnedSource, onSave, onCancel }) => {
  const [correction, setCorrection] = useState({
    server: learnedSource?.server || servers[0] || '',
    database: learnedSource?.database || '',
    table: learnedSource?.table || '',
    column: learnedSource?.column || ''
  });
  const [error, setError] = useState(null);

  // Debug logging
  useEffect(() => {
    console.log('[CorrectionForm] Schema structure:', schemaStructure);
    console.log('[CorrectionForm] Servers:', servers);
  }, [schemaStructure, servers]);

  // Safely access schema structure with error handling
  const getServerData = () => {
    if (!correction.server || !schemaStructure) return null;
    return schemaStructure[correction.server];
  };

  const databases = (() => {
    try {
      const serverData = getServerData();
      if (!serverData?.databases) return [];
      return Object.keys(serverData.databases);
    } catch (e) {
      console.error('[CorrectionForm] Error getting databases:', e);
      return [];
    }
  })();

  const tables = (() => {
    try {
      const serverData = getServerData();
      if (!serverData?.databases?.[correction.database]?.tables) return [];
      return Object.keys(serverData.databases[correction.database].tables);
    } catch (e) {
      console.error('[CorrectionForm] Error getting tables:', e);
      return [];
    }
  })();

  const columns = (() => {
    try {
      const serverData = getServerData();
      const tableData = serverData?.databases?.[correction.database]?.tables?.[correction.table];
      if (!tableData?.columns) return [];
      return tableData.columns;
    } catch (e) {
      console.error('[CorrectionForm] Error getting columns:', e);
      return [];
    }
  })();

  if (!servers || servers.length === 0) {
    return (
      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">No servers available. Please ensure schema is discovered.</p>
        <Button size="sm" variant="outline" onClick={onCancel} className="mt-2">
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
      {/* Explanation of what user is correcting */}
      <div className="mb-4 p-3 bg-white rounded border border-amber-100">
        <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
          <Edit2 className="w-4 h-4" />
          You are correcting the SOURCE table (green box)
        </h4>
        <p className="text-sm text-gray-600">
          The AI suggested <span className="font-semibold text-green-700">{currentKey?.sourceTable}.{currentKey?.sourceColumn}</span> as the source, 
          but if that's wrong, select the correct table below.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Target stays: <span className="font-medium text-blue-600">{currentTable?.table}.{currentTable?.column || currentKey?.column}</span>
        </p>
      </div>

      <h4 className="font-medium text-amber-800 mb-3">Select the correct SOURCE table:</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Server</label>
          <Select 
            value={correction.server} 
            onValueChange={v => {
              setCorrection({ server: v, database: '', table: '', column: '' });
              setError(null);
            }}
          >
            <SelectTrigger><SelectValue placeholder="Server" /></SelectTrigger>
            <SelectContent>
              {servers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Database</label>
          <Select 
            value={correction.database} 
            onValueChange={v => {
              setCorrection({ ...correction, database: v, table: '', column: '' });
              setError(null);
            }} 
            disabled={!correction.server || databases.length === 0}
          >
            <SelectTrigger><SelectValue placeholder={databases.length === 0 ? "No databases" : "Database"} /></SelectTrigger>
            <SelectContent>
              {databases.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Table</label>
          <Select 
            value={correction.table} 
            onValueChange={v => {
              setCorrection({ ...correction, table: v, column: '' });
              setError(null);
            }} 
            disabled={!correction.database || tables.length === 0}
          >
            <SelectTrigger><SelectValue placeholder={tables.length === 0 ? "No tables" : "Table"} /></SelectTrigger>
            <SelectContent>
              {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Column (Primary Key)</label>
          <Select 
            value={correction.column} 
            onValueChange={v => {
              setCorrection({ ...correction, column: v });
              setError(null);
            }} 
            disabled={!correction.table || columns.length === 0}
          >
            <SelectTrigger><SelectValue placeholder={columns.length === 0 ? "No columns" : "Column"} /></SelectTrigger>
            <SelectContent>
              {columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Preview of the corrected relationship */}
      {correction.column && (
        <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded text-sm">
          <span className="text-green-700">New relationship: </span>
          <span className="font-medium">{correction.server}.{correction.database}.{correction.table}.{correction.column}</span>
          <span className="text-gray-500"> ← {currentTable?.table}.{currentTable?.column || currentKey?.column}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}
      
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(correction)} disabled={!correction.column}>
          <Check className="w-4 h-4 mr-1" /> Save Correction
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};


// Complex Mapping inline form - for conditional/polymorphic relationships
const ComplexMappingInlineForm = ({ currentKey, currentTable, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: `${currentKey?.column || 'column'} conditional mapping`,
    condition_column: '',
    description: '',
    mappings: [{ condition_value: '', target_table: '', target_column: '' }]
  });
  const [saving, setSaving] = useState(false);

  const addMappingRow = () => {
    setForm(prev => ({
      ...prev,
      mappings: [...prev.mappings, { condition_value: '', target_table: '', target_column: '' }]
    }));
  };

  const updateMappingRow = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      mappings: prev.mappings.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };

  const removeMappingRow = (index) => {
    if (form.mappings.length > 1) {
      setForm(prev => ({
        ...prev,
        mappings: prev.mappings.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSave = async () => {
    if (!form.condition_column) {
      toast.error('Please specify the condition column');
      return;
    }
    
    // Convert mappings array to object
    const mappingsObj = {};
    for (const m of form.mappings) {
      if (m.condition_value && m.target_table) {
        mappingsObj[m.condition_value] = {
          table: m.target_table,
          column: m.target_column || currentKey?.column || 'id'
        };
      }
    }
    
    if (Object.keys(mappingsObj).length === 0) {
      toast.error('Please add at least one mapping');
      return;
    }
    
    setSaving(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/learning/complex-mapping`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          name: form.name,
          source_column: currentKey?.column || 'unknown',
          condition_column: form.condition_column,
          description: form.description || `Conditional mapping for ${currentKey?.column} based on ${form.condition_column}`,
          mappings: mappingsObj,
          database: currentTable?.database || 'default'
        })
      }).then(r => r.json());
      
      if (response.success) {
        onSave();
      } else {
        toast.error(response.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <GitBranch className="w-5 h-5 text-orange-600" />
        <h4 className="font-medium text-orange-800">Define Conditional Mapping</h4>
      </div>
      
      <p className="text-sm text-orange-700 mb-3">
        Use this when <code className="bg-orange-100 px-1 rounded">{currentKey?.column}</code> maps to 
        different tables based on another column's value.
      </p>
      
      <div className="space-y-3">
        <div>
          <label className="text-sm text-gray-600">Condition Column (which column determines the target?)</label>
          <input
            type="text"
            value={form.condition_column}
            onChange={(e) => setForm(prev => ({ ...prev, condition_column: e.target.value }))}
            placeholder="e.g., vendor, platform, type"
            className="w-full p-2 border rounded mt-1"
          />
        </div>
        
        <div>
          <label className="text-sm text-gray-600 mb-2 block">Mappings (add conditions)</label>
          <div className="space-y-2">
            {form.mappings.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 w-6">If</span>
                <input
                  type="text"
                  value={m.condition_value}
                  onChange={(e) => updateMappingRow(idx, 'condition_value', e.target.value)}
                  placeholder="value"
                  className="w-24 p-1.5 border rounded text-sm"
                />
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={m.target_table}
                  onChange={(e) => updateMappingRow(idx, 'target_table', e.target.value)}
                  placeholder="target table"
                  className="flex-1 p-1.5 border rounded text-sm"
                />
                <span className="text-gray-400">.</span>
                <input
                  type="text"
                  value={m.target_column}
                  onChange={(e) => updateMappingRow(idx, 'target_column', e.target.value)}
                  placeholder="column"
                  className="w-24 p-1.5 border rounded text-sm"
                />
                {form.mappings.length > 1 && (
                  <button 
                    onClick={() => removeMappingRow(idx)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addMappingRow} className="mt-1">
              + Add Condition
            </Button>
          </div>
        </div>
        
        <div>
          <label className="text-sm text-gray-600">Description (optional)</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Explain the logic in plain English..."
            className="w-full h-16 p-2 border rounded mt-1 text-sm"
          />
        </div>
      </div>
      
      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
          Save Conditional Mapping
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};


export default RelationshipWizard;
